import React, { useEffect, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  ScrollText,
  UserPlus,
  UserCog,
  UserX,
  FileText,
  CircleCheckBig,
  Settings,
  RotateCcw,
  Activity,
} from "lucide-react";

const ACTIONS = {
  create_user: { label: "Buat User", color: "bg-emerald-100 text-emerald-800", chip: "bg-emerald-500", icon: UserPlus },
  update_user: { label: "Ubah User", color: "bg-sky-100 text-sky-800", chip: "bg-sky-500", icon: UserCog },
  delete_user: { label: "Hapus User", color: "bg-rose-100 text-rose-800", chip: "bg-rose-500", icon: UserX },
  create_surat: { label: "Buat Surat", color: "bg-brand/10 text-brand-700", chip: "bg-brand", icon: FileText },
  update_surat: { label: "Ubah Surat", color: "bg-sky-100 text-sky-800", chip: "bg-sky-500", icon: FileText },
  delete_surat: { label: "Hapus Surat", color: "bg-rose-100 text-rose-800", chip: "bg-rose-500", icon: UserX },
  update_status: { label: "Ubah Status", color: "bg-amber-100 text-amber-800", chip: "bg-amber-500", icon: CircleCheckBig },
  update_numbering: { label: "Ubah Pengaturan", color: "bg-teal-100 text-teal-800", chip: "bg-teal-600", icon: Settings },
  reset_numbering: { label: "Reset Counter", color: "bg-slate-200 text-slate-800", chip: "bg-slate-700", icon: RotateCcw },
  set_next_number: { label: "Set Nomor Berikutnya", color: "bg-indigo-100 text-indigo-800", chip: "bg-indigo-500", icon: FileText },
};

const AVATAR_COLORS = [
  "bg-sky-100 text-sky-700",
  "bg-amber-100 text-amber-700",
  "bg-emerald-100 text-emerald-700",
  "bg-rose-100 text-rose-700",
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
];
function avatarClass(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) % 9973;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  return (name || "?").slice(0, 2).toUpperCase();
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit-logs");
        setLogs(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <PageHeader
        overline="Administrasi"
        title="Audit Log"
        description="Riwayat seluruh perubahan penting yang terjadi di sistem."
        icon={ScrollText}
        tone="success"
      />

      {loading && <div className="text-sm text-ink-500">Memuat…</div>}

      {!loading && logs.length === 0 && (
        <Card className="border-border shadow-none p-12 text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-50 text-emerald-600">
            <Activity className="h-6 w-6" />
          </div>
          <p className="mt-3 font-heading text-lg font-semibold text-ink-900">Belum ada catatan log</p>
          <p className="mt-1 text-sm text-ink-500">Setiap aktivitas penting (buat user, ubah status, dll) akan tercatat di sini.</p>
        </Card>
      )}

      {!loading && logs.length > 0 && (
        <Card className="overflow-hidden border-border shadow-none">
          <ol className="relative divide-y divide-border">
            {logs.map((l) => {
              const meta = ACTIONS[l.action] || { label: l.action, color: "bg-slate-100 text-slate-800", chip: "bg-slate-500", icon: Activity };
              const Icon = meta.icon;
              return (
                <li key={l.id} className="relative p-5 transition-colors hover:bg-secondary/30" data-testid={`log-${l.id}`}>
                  <div className="flex items-start gap-4">
                    <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ${meta.chip} text-white shadow-sm`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={`${meta.color} hover:${meta.color}`}>{meta.label}</Badge>
                        <span className="font-mono text-sm font-medium text-ink-900">{l.target}</span>
                        <span className="text-xs text-ink-500">·</span>
                        <span className="text-xs text-ink-500">{formatTanggal(l.created_at)}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className={`grid h-6 w-6 place-items-center rounded-full text-[10px] font-semibold ${avatarClass(l.actor_username)}`}>
                          {initials(l.actor_username)}
                        </span>
                        <span className="text-ink-500">
                          oleh <span className="font-medium text-ink-700">@{l.actor_username}</span>
                        </span>
                      </div>

                      {(l.before || l.after) && (
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          {l.before && (
                            <div className="rounded-md border border-rose-200 bg-rose-50/40 p-3">
                              <p className="overline text-rose-700 mb-1">Sebelum</p>
                              <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-ink-700">
                                {JSON.stringify(l.before, null, 2)}
                              </pre>
                            </div>
                          )}
                          {l.after && (
                            <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
                              <p className="overline text-emerald-700 mb-1">Sesudah</p>
                              <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-ink-700">
                                {JSON.stringify(l.after, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </Card>
      )}
    </div>
  );
}
